export const QUESTIONNAIRE_NAV_TITLE_MAX_LENGTH = 30;
export const QUESTIONNAIRE_PAGE_TITLE_MAX_LENGTH = 60;
export const QUESTIONNAIRE_BUTTON_TEXT_MAX_LENGTH = 40;
/** Max visible sub-group tabs; extras are truncated (see console warn in tab component). */
export const QUESTIONNAIRE_SUB_GROUP_TAB_MAX = 5;
export const QUESTIONNAIRE_LOADING_SPINNER_DIAMETER = 40;
export const QUESTIONNAIRE_RESPONSE_DELETED_STATUS = 'stopped';

/**
 *
 */
export function truncateQuestionnaireLabel(value: string | null | undefined, maxLength: number): string {
    if (!value) {
        return '';
    }
    if (value.length <= maxLength) {
        return value;
    }
    return `${value.slice(0, maxLength).trimEnd()}...`;
}

export interface QuestionnaireGroupsConfig {
    questionnaireGroups: QuestionnaireGroup[];
}

export interface QuestionnaireGroup {
    id: string;
    isVisible: boolean;
    navigationTitle: string;
    navigationIcon: string | null;
    pageTitle: string;
    order: number;
    navigationUrl: string;
    questionnaireSubGroups: QuestionnaireSubGroup[];
    operationGroups?: QuestionnaireOperationGroup[];
}

/** Where QuestionnaireResponses are loaded from */
export type QuestionnaireResponseScope = 'encounter' | 'patient';

export interface QuestionnaireSubGroup {
    order: number;
    displayName: string;
    isVisible: boolean;
    questionnaireButtonText: string;
    icon: string | null;
    tag: string;
    /**
     * Controls FHIR list fetch scope.
     * - encounter (default): responses for current encounter only
     * - patient: responses across encounters for the same patient
     * Create/edit still bind to the current encounter.
     */
    responseScope?: QuestionnaireResponseScope;
    questionnaireColumnInformations: QuestionnaireColumnInformation[];
    operationGroups?: QuestionnaireOperationGroup[];
}

/** Per-questionnaire source_key/is_link_id when structure differs within same tag */
export interface QuestionnaireColumnSourceOverride {
    id?: string;
    questionnaireUrl: string;
    sourceKey: string;
    isLinkId: boolean;
}

export interface QuestionnaireColumnInformation {
    order: number;
    displayName: string;
    sourceKey: string;
    valueType: string | string[];
    fallbackText: string;
    isLinkId: boolean;
    isVisible: boolean;
    entityType: 'questionnaire' | 'questionnaireResponse';
    questionnaireColumnsMapping: questionnaireColumnsMapping[];
    customFormatting?: Record<string, any>;
    /** Optional overrides keyed by FHIR questionnaire URL */
    sourceOverrides?: QuestionnaireColumnSourceOverride[];
}

export interface questionnaireColumnsMapping {
    displayName: string;
    fhirValue: string;
    isVisible: boolean;
}

export interface QuestionnaireOperationGroup {
    order: number;
    displayName: string;
    isVisible: boolean;
    groupType: QuestionnaireOperationGroupType;
    operations: QuestionnaireOperation[];
    isAutofill?: boolean;
    behaviorType?: QuestionnaireOperationBehaviorType | string | null;
}

export interface QuestionnaireOperation {
    order: number;
    displayName: string;
    isVisible: boolean;
    isDefault: boolean;
    sourceKey: string | null;
    fhirValue: any;
    valueType: string | null;
    isLinkId: boolean;

    entityType?: 'questionnaire' | 'questionnaireResponse';
    sortType?: QuestionnaireOperationSortType;
    groupFormattingType?: 'string' | 'date';
    behaviorType?: QuestionnaireOperationBehaviorType | string | null;
    displayNamesMapping?: Record<string, string>;
}

export enum QuestionnaireOperationGroupType {
    Filter = 'filter',
    Group = 'group',
    Sort = 'sort',
}

export enum QuestionnaireOperationSortType {
    Alphabetical = 'alphabetical',
    Date = 'date',
}

export enum QuestionnaireOperationGroupFormattingType {
    String = 'string',
    Date = 'date',
}

/** Allowed OperationGroup.behavior_type values from BE schema */
export enum QuestionnaireOperationBehaviorType {
    ExactMatch = 'exact_match',
    DateExact = 'date_exact',
    DatetimeRoundHour = 'datetime_round_hour',
    DatetimeRoundDay = 'datetime_round_day',
    StringNormalized = 'string_normalized',
}

// Utility types for better type safety
export interface QuestionnaireRendererDialogData<T = any> {
    questionnaireResponse: T;
    isReadOnly: boolean;
}

export interface QuestionnaireFilterChangeEvent {
    operation: QuestionnaireOperation;
    filterStateKey: string;
    groupType: QuestionnaireOperationGroupType;
}

export interface GroupedQuestionnaireResponses<T = any> {
    group: string;
    responses: T[];
}
